import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class MenuService {
	private readonly STORAGE_KEY = 'menuState'; // Key used to store menu states in localStorage

	// BehaviorSubject to track the menu state
	private menuStateSubject = new BehaviorSubject<{ [key: string]: boolean }>(this.loadState());

	menuState$ = this.menuStateSubject.asObservable();

	/**
	 * Toggles the state of a specific menu and updates localStorage.
	 * @param menuKey - The key identifying the menu (e.g., 'sidebar', 'profileMenu').
	 */
	toggleMenu(menuKey: string): void {
		const currentState = this.menuStateSubject.value;
		const newState = {
			...currentState,
			[menuKey]: !currentState[menuKey],
		};
		this.menuStateSubject.next(newState);
		this.saveState(newState);
	}

	/**
	 * Opens a specific menu and updates localStorage.
	 * @param menuKey - The key identifying the menu to open.
	 */
	openMenu(menuKey: string): void {
		const currentState = this.menuStateSubject.value;
		const newState = {
			...currentState,
			[menuKey]: true,
		};
		this.menuStateSubject.next(newState);
		this.saveState(newState);
	}

	/**
	 * Closes a specific menu and updates localStorage.
	 * @param menuKey - The key identifying the menu to close.
	 */
	closeMenu(menuKey: string): void {
		const currentState = this.menuStateSubject.value;
		const newState = {
			...currentState,
			[menuKey]: false,
		};
		this.menuStateSubject.next(newState);
		this.saveState(newState);
	}

	/**
	 * Loads the menu state from localStorage.
	 * If no state is found, defaults are returned.
	 * @returns An object representing the menu state.
	 */
	private loadState(): { [key: string]: boolean } {
		const savedState = localStorage.getItem(this.STORAGE_KEY);
		return savedState ? JSON.parse(savedState) : { sidebar: false, profileMenu: false, backoffice: true }; // Default values
	}

	/**
	 * Saves the current menu state to localStorage.
	 * @param state - The state object to be saved.
	 */
	private saveState(state: { [key: string]: boolean }): void {
		localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
	}

	/**
	 * Retrieves the current state of a specific menu.
	 * @param menuKey - The key identifying the menu to retrieve.
	 * @returns A boolean representing the menu's state.
	 */
	getMenuState(menuKey: string): boolean {
		return this.menuStateSubject.value[menuKey];
	}
}
